import { Alert, Autocomplete, AutocompleteChangeReason, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogContent, DialogContentText, DialogProps, DialogTitle, FormControlLabel, FormGroup, InputAdornment, LinearProgress, Stack, TextField } from "@mui/material";
import { AccountCircle, Edit, Group, Public, Visibility, Lock, Business } from "@mui/icons-material";
import React, { ReactElement, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useCallback } from "react";
import { useState } from "react";
import { useNavigate } from "react-router";
import useAuth from "system/APIAuth";
import APIRequest, { APIError } from "system/APIRequest";
import i18n from "translation";
import { ActionColors, ResourceActionIcon } from "./ResourceActionButtonGroup";
import { permissionTest } from "system/InfegyUser";
import CopyToClipboard from "components/uiElements/inputs/buttons/CopyToClipboardButton";

export interface ResourceActionDialogProps {
  action: "share" | "unshare" | "duplicate" | "create-from" | "remove" | "restore" | "reassign" | "logout-sessions",
  resourceIds: string[] | string,
  endpoint: string,
  handleComplete?: (wasSuccessful: boolean, resourceIds: string[], action: string, error?: APIError) => void,
  asAdmin?: boolean,
  showSharedWarning?: boolean,
  reassignAccount?: boolean,
  defaultAccountId?: string,
  accountAccess?: string, // Account access level, used for Share configuration
  reassignable?: boolean,
  existingShares?: []
}

export function ResourceActionDialog(props: DialogProps & ResourceActionDialogProps) {
  const auth = useAuth();
  let navigate = useNavigate();

  const [isRunning, setIsRunning] = useState(false);
  const [totalCompleted, setTotalCompleted] = useState(0);
  const [apiError, setApiError] = useState<APIError | undefined>();
  const [shareTargetsLoading, setShareTargetsLoading] = useState(false);
  const [currentSharesLoading, setCurrentSharesLoading] = useState(false);
  const [shareTargets, setShareTargets] = useState<any>({ account: [], team: [], user: [] });
  const [shareAccountId, setShareAccountId] = useState<string | undefined>(props.defaultAccountId);
  const [selectedTeamShares, setSelectedTeamShares] = useState<any[]>([]);
  const [selectedUserShares, setSelectedUserShares] = useState<any[]>([]);
  const [selectedReassignUser, setSelectedReassignUser] = useState<any | null>(null);
  const [shareWithEditing, setShareWithEditing] = useState(false);
  const [selectedAccountAccess, setSelectedAccountAccess] = useState(props.accountAccess);
  const [initialShares, setInitialShares] = useState<object[]>([]);
  const [resource, setResource] = useState<any>();

  // If we are sharing or duplicating, we need to get our share targets (who we can share with)
  const loadShareTargets = useCallback(async () => {
    // We only need to do this for some actions
    if (props.action !== "share" && props.action !== "duplicate" && props.action !== "reassign")
      return;

    setShareTargetsLoading(true);

    await APIRequest("share-targets", { "account_id": shareAccountId || "", "all_teams": (props.asAdmin ? "1" : "0") }).then((response: any) => {
      setShareTargets(response);
    }).catch((reason: APIError) => {
      setApiError(reason);
    }).finally(() => {
      setShareTargetsLoading(false);
    });

  }, [props.action, props.asAdmin, shareAccountId]);

  // If sharing, we load the current shares on the resource
  const loadCurrentShares = useCallback(async () => {
    if (props.action !== "share")
      return;

    let resourceId = props.resourceIds;
    if(Array.isArray(props.resourceIds)) {
      if(props.resourceIds.length > 1)
        return;
      else
      resourceId = props.resourceIds[0];
    }

    setCurrentSharesLoading(true);

    const params: object = {};
    if(props.asAdmin) {
      params["admin"] = "1";
    }

    await APIRequest(`obj/${props.endpoint}/${resourceId}`, params).then((response: any) => {
      setResource(response.output);
      if(Array.isArray(response['output']['_shares'])) {
        const teamShares: object[] = [];
        const userShares: object[] = [];
        const shares: object[] = [];
        for(const share of response['output']['_shares']) {
          shares.push({
            id: share['id'],
            to_id: share['to_id']
          });
          if(share['type'] === 'user') {
            userShares.push({
              id: share['to_id'],
              email: share['to_user'],
              name: share['to_name']
            })
          } else if(share['type'] === 'team') {
            teamShares.push({
              id: share['to_id'],
              name: share['to_name']
            })
          }
        }

        setInitialShares(shares);
        setSelectedUserShares(userShares);
        setSelectedTeamShares(teamShares);

        if(response['output']['access']) {
          setSelectedAccountAccess(response['output']['access']);
        }
      }
    }).catch((reason: APIError) => {
      setApiError(reason);
    }).finally(() => {
      setCurrentSharesLoading(false);
    });

  }, [props.action, props.asAdmin, shareAccountId]);

  useEffect(() => {
    if (["share", "duplicate", "reassign"].includes(props.action)) {
      loadShareTargets();
      if(props.action === "share") {
        // For share, also load existing shares
        loadCurrentShares();
      }
    }
  }, [loadShareTargets, props.action])

  if (props.action === "create-from") {
    // This dialog shouldn't be opened with this action
    // It exists in the type for use with the ResourceActionButtonGroup
    console.error("ResourceActionDialog called with create-from action, not intended to be used here");
    Sentry.captureMessage("ResourceActionDialog called with create-from action, not intended to be used here");
    return <React.Fragment />;
  }

  // Break out our custom props, so we can pass rest to the Dialog component without errors
  let { action, resourceIds, endpoint, handleComplete, asAdmin, showSharedWarning, reassignAccount, defaultAccountId, accountAccess, reassignable, existingShares, ...rest } = props;

  // Ensure resourceIds is always an array
  if (typeof resourceIds === "string")
    resourceIds = [resourceIds];

  const performRequests = async () => {
    setIsRunning(true);
    setTotalCompleted(0);
    setApiError(undefined);

    // Build request parameters
    let requestURI = "";  // Default is blank, set in loop, here to allow override
    let requestParams: { [key: string]: string; } = {};
    let requestMethod = "PUT";
    let idParam: string | undefined;

    if (props.asAdmin) {
      requestParams["admin"] = "1";
    }

    // Set to true if only shares were removed to avoid running the default action with no input
    let skipAction = false;

    // Store a possible API error from removing shares or the main action
    let localError: APIError | void | undefined;

    if (props.action === "reassign") {
      if (reassignAccount) {
        requestParams["to_account"] = shareAccountId || ""; // || "" just here to fix a typescript error, this should be an impossible state to reach
      } else {
        requestParams["to_user"] = selectedReassignUser.id;
      }
    } else if (props.action === "share" || props.action === "duplicate") {
      requestMethod = "POST";

      if (props.action === "share") {
        const removedShareIds: string[] = [];
        const initialShareIds: string[] = initialShares.map((share => share['to_id']));
        skipAction = true;

        // If we had initial shares, we need to check if any were removed
        if(initialShareIds.length) {
          const currentShareIds: string[] = [];
          for(const share of selectedTeamShares) {
            currentShareIds.push(share['id']);
          }
          for(const share of selectedUserShares) {
            currentShareIds.push(share['id']);
          }

          for(const share of initialShares) {
            if(!currentShareIds.includes(share['to_id'])) {
              removedShareIds.push(share['id']);
            }
          }

          // Do we have shares we need to delete?
          if(removedShareIds.length) {
            const promises = removedShareIds.map((shareId): Promise<any> => {
              return APIRequest(`obj/shared-resource/${shareId}`, requestParams, { method: 'DELETE' });
            });
            try {
              await Promise.all(promises);
            } catch (e: any) {
              const error = e as APIError;
              setApiError(error);
            }
          }
        }

        if (selectedUserShares && selectedAccountAccess !== "account-modify") {
          const filtered = selectedUserShares.filter((x) => !initialShareIds.includes(x.id));
          if(filtered.length) {
            requestParams["to_user"] = filtered.map(x => x.id).join(",");
            skipAction = false;
          }
        }
        if (selectedTeamShares && props.action === "share" && selectedAccountAccess !== "account-modify") {
          const filtered = selectedTeamShares.filter((x) => !initialShareIds.includes(x.id));
          if(filtered.length) {
            requestParams["to_team"] = filtered.map(x => x.id).join(",");
            skipAction = false;
          }
        }
        if (shareWithEditing) {
          requestParams["edit_permission"] = "1";
        }
        if (selectedAccountAccess && props.accountAccess !== selectedAccountAccess && selectedAccountAccess !== "multiple") {
          requestParams["access"] = selectedAccountAccess;
          skipAction = false;
        }
      } else if (props.action === "duplicate") {
        requestParams["to_user"] = selectedUserShares.map(x => x.id).join(",");
      }
    } else if (props.action === "remove" || props.action === "unshare") {
      requestMethod = "DELETE";
    } else if (props.action === "logout-sessions") {
      requestURI = "logout-sessions";
      idParam = "user_id";
    }

    if(!skipAction) {
      for (const id of resourceIds) {
        if (idParam) {
          requestParams[idParam] = id;
        }

        // await will run one at a time, allowing us to abort on error
        localError = await APIRequest(requestURI || ('obj/' + props.endpoint + '/' + id + '/' + props.action), requestParams, { method: requestMethod }).then((response: any) => {
          // Done, nothing left to do!
        }).catch((reason: APIError) => {
          return reason;
        }).finally(() => {
          setTotalCompleted((prevState: number): number => {
            return prevState + 1;
          });
        });

        if (localError) {
          setApiError(localError);
          break;
        }
      }
    }

    setIsRunning(false);

    if (!localError) {
      // Success!
      // Clear selected share targets (if any) so we get a new form on re-open
      setSelectedUserShares([]);
      setSelectedUserShares([]);
      setSelectedReassignUser(null);

      props.handleComplete?.(true, resourceIds as string[], props.action);
    } // We report complete on close for errors
  }

  const handleConfirmationClick = (e: any) => {
    if (!isRunning) {
      performRequests();
    }
  }

  const handleClose = (e: {}, reason: "escapeKeyDown" | "backdropClick") => {
    if (!isRunning) {
      // On close, clear state, incase we're re-open
      setApiError(undefined);
      setTotalCompleted(0);
      setIsRunning(false);

      if (apiError) {
        // We report complete on close for errors
        props.handleComplete?.(false, resourceIds as string[], props.action, apiError);
      }

      if (props.onClose) {
        props.onClose(e, reason);
      }
    }
  }

  const actionTextKey = props.reassignAccount ? "reassign-account" : props.action;
  const itemCount = resourceIds.length;
  const titleKey = "resource-action.dialog." + actionTextKey + "-title";
  const bodyKey = "resource-action.dialog." + actionTextKey + (props.action === "duplicate" && props.reassignable === false ? "-noreassign" : "") + (itemCount === 1 ? "-single" : "-template") + "-body";

  const showShareOptions = props.action === "share" || props.action === "reassign" ||
                           (props.action === "duplicate" && (props.reassignable === true));
  const iconMarginFix = { marginBottom: "-5px" };

  const dashboardLink = `https://starscape.infegy.com/dashboard/${props.resourceIds}`;

  // Check if the user has added or removed any shares, differing from the initial state (if any)
  let matchesInitialState = false;
  if(initialShares.length && initialShares.length == (selectedTeamShares.length + selectedUserShares.length) && selectedAccountAccess === props.accountAccess) {
    const currentShareIds: string[] = [];
    for(const share of selectedTeamShares) {
      currentShareIds.push(share['id']);
    }
    for(const share of selectedUserShares) {
      currentShareIds.push(share['id']);
    }

    const initialShareIds: string[] = initialShares.map((share => share['to_id']));
    if(currentShareIds.every(id => initialShareIds.includes(id))) {
      matchesInitialState = true;
    }
  }

  // This logic checks if we should disable the confirmation button
  let confirmationDisabled = isRunning || apiError !== undefined || matchesInitialState ||
    (showShareOptions &&
      (shareTargetsLoading || ((
          (!(selectedTeamShares.length + selectedUserShares.length + initialShares.length) && !selectedReassignUser && !reassignAccount) ||
          (reassignAccount && !shareAccountId)) &&
          (selectedAccountAccess === props.accountAccess)
        )));

  // Dashboards, with the right permission, can gain a "public" sharing option
  const accessOptions =  ['restricted','account-read','account-modify'];
  if (props.endpoint === "dashboard" && permissionTest("dashboard_web_sharing") && resource?.template_type !== "single") {
      accessOptions.push('public');
  }

  return (
    <Dialog {...rest} onClose={handleClose}
      onClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
      onKeyDown={event => event.stopPropagation()} >
      <DialogTitle color={ActionColors[props.action]}>
        <ResourceActionIcon action={props.action} style={iconMarginFix} color={ActionColors[props.action]} />
        &nbsp;{i18n.t(titleKey)}
      </DialogTitle>
      <DialogContent>

        <DialogContentText style={{ marginBottom: "1rem", whiteSpace: "pre-wrap" }}>
          {i18n.t(bodyKey).replace('${itemCount}', itemCount.toString())}
        </DialogContentText>

        {props.showSharedWarning && props.action === "remove" &&
          <Alert severity="warning">
            {i18n.t("resource-action.dialog." + (resourceIds.length > 1 ? "shared-warning-many" : "shared-warning-single"))}
          </Alert>
        }

        {showShareOptions &&
          <FormGroup>
            <Stack spacing={2}>

              {props.asAdmin && (props.action === "duplicate" || props.action == "reassign") && shareTargets?.account?.length > 1 &&
                <Autocomplete openOnFocus autoSelect autoHighlight
                  clearOnBlur={false}
                  options={shareTargets?.account}
                  onChange={(event: object, value: any | null, reason: string) => {
                    if (value) {
                      setShareAccountId(value.id);
                      setSelectedTeamShares([]);
                      setSelectedUserShares([]);
                      setSelectedReassignUser(null);
                    }
                  }}
                  value={shareTargets?.account.find((v: any) => v.id === shareAccountId) || null}
                  disabled={!shareTargets?.account?.length || isRunning}
                  getOptionLabel={(option: any) => option.name}
                  isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                  renderInput={(params: any) => (<TextField {...params}
                    autoComplete="off"
                    variant="standard"
                    label={i18n.t("resource-action.active-account")} />
                  )}
                />
              }

              {showShareOptions && !shareTargetsLoading &&
                <>
                  {props.action === "share" &&
                    <Autocomplete
                      openOnFocus
                      disableClearable={true}
                      value={selectedAccountAccess ? selectedAccountAccess : undefined}
                      onChange={(event: any, newValue: string | null) => {
                        setSelectedAccountAccess(newValue || selectedAccountAccess);
                      }}
                      inputValue={selectedAccountAccess ? i18n.t("resource-action.dialog.share-account-access-short-" + selectedAccountAccess) : undefined}
                      getOptionLabel={(option) => i18n.t("resource-action.dialog.share-account-access-short-" + option)}
                      isOptionEqualToValue={(option: any, value: any) => option === value}
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          autoComplete="off"
                          variant="standard"
                          label={i18n.t("resource-action.dialog.share-account-access-title")}
                          placeholder={selectedAccountAccess ? undefined : i18n.t("resource-action.dialog.share-account-access-short-multiple")}
                          slotProps={{
                            ...params.slotProps,

                            input: {
                              ...params.slotProps.input,
                              startAdornment: ( 
                                <InputAdornment position="start">
                                  {selectedAccountAccess === "restricted" && <Lock color="error" />}
                                  {selectedAccountAccess === "account-read" && <Visibility color="info" />}
                                  {selectedAccountAccess === "account-modify" && <Business color="warning" />}
                                  {selectedAccountAccess === "public" && <Public color="success" />}
                                </InputAdornment>
                              )
                            }
                          }}
                        />
                      )}
                      renderOption={(_props, option) => {
                        const iconMap: Record<string, ReactElement> = {
                          "restricted": <Lock style={iconMarginFix} color="error" />,
                          "account-read": <Visibility style={iconMarginFix} color="info" />,
                          "account-modify": <Business style={iconMarginFix} color="warning" />,
                          "public": <Public style={iconMarginFix} color="success" />,
                        };
                        return (
                          <li {..._props} key={option}>
                            <Box sx={{ '& > svg': { mr: 1 } }}>
                              {(iconMap)[option]}
                              {i18n.t("resource-action.dialog.share-account-access-desc-" + option)}
                            </Box>
                          </li>
                        );
                      }}
                      id="account-access-selector"
                      options={accessOptions}
                    />
                  }

                  {props.action !== "reassign" && (props.action !== "share" || selectedAccountAccess !== "account-modify") &&
                    <>
                      {props.action === "share" &&
                        <Autocomplete openOnFocus autoComplete autoHighlight disableCloseOnSelect
                          multiple
                          options={shareTargets?.team}
                          disabled={!shareTargets?.team?.length || isRunning}
                          getOptionLabel={(option: any) => option.name}
                          onChange={(event: React.SyntheticEvent<Element, Event>, value: any[], reason: AutocompleteChangeReason) => { setSelectedTeamShares(value); }}
                          value={selectedTeamShares}
                          renderInput={(params: any) => (<TextField {...params}
                            autoComplete="off"
                            variant="standard"
                            label={i18n.t(shareTargets?.team.length > 0 ? "resource-action.share-with-teams" : "resource-action.no-teams")} />
                          )}
                          isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                          renderValue={(value, getItemProps) =>
                            value.map((option, index) => (
                              <Chip label={option.name} icon={<Group />} {...getItemProps({ index })} key={option.id} />
                            ))
                          }
                        />
                      }

                      <Autocomplete openOnFocus autoComplete autoHighlight disableCloseOnSelect
                        multiple
                        options={shareTargets?.user}
                        disabled={!shareTargets?.user?.length || isRunning}
                        getOptionLabel={(option: any) => `${option.name} (${option.email})`}
                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                        onChange={(event: React.SyntheticEvent<Element, Event>, value: any[], reason: AutocompleteChangeReason) => { setSelectedUserShares(value); }}
                        value={selectedUserShares}
                        renderInput={(params: any) => (<TextField {...params}
                          autoComplete="off"
                          variant="standard"
                          label={i18n.t(shareTargets?.user.length > 0 ? ("resource-action." + (props.action === "share" ? "share-with-users" : "duplicate-to-users")) : "resource-action.no-users")} />
                        )}
                        renderValue={(value, getItemProps) =>
                          value.map((option, index) => (
                            <Chip label={option.name} icon={<AccountCircle />} {...getItemProps({ index })} key={option.id} />
                          ))
                        }
                      />
                    </>
                  }

                  {props.action === "reassign" && !reassignAccount &&
                    <Autocomplete openOnFocus autoComplete autoHighlight disableClearable
                      options={shareTargets?.user}
                      disabled={!shareTargets?.user?.length || isRunning}
                      getOptionLabel={(option: any) => `${option.name} (${option.email})`}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                      onChange={(event: React.SyntheticEvent<Element, Event>, value: any, reason: AutocompleteChangeReason) => { setSelectedReassignUser(value); }}
                      value={selectedReassignUser}
                      renderInput={(params: any) => (<TextField {...params}
                        autoComplete="off"
                        variant="standard"
                        label={i18n.t(shareTargets?.user.length > 0 ? "resource-action.select-user" : "resource-action.no-users")} />
                      )}
                    />
                  }
                </>
              }

              {props.action === "share" && selectedAccountAccess !== "account-modify" &&
                <FormControlLabel control={
                  <Checkbox
                    disabled={isRunning}
                    checked={shareWithEditing}
                    onClick={(e: any) => setShareWithEditing(!shareWithEditing)} />}
                  label={<><Edit style={iconMarginFix} /> {i18n.t("resource-action.share-with-write-access")}</>} />
              }

              {props.action === "share" && props.endpoint === "dashboard" &&
                <CopyToClipboard text={dashboardLink} buttonLabel={i18n.t("resource-action.copy-dashboard-url")}/>
              }

            </Stack>

          </FormGroup>
        }

        {showShareOptions && shareTargetsLoading &&
          <CircularProgress style={{ margin: "0 auto" }} />
        }

        {isRunning &&
          <div>
            <LinearProgress value={resourceIds.length < 2 ? undefined : (totalCompleted / resourceIds.length) * 100} variant={resourceIds.length < 2 ? "indeterminate" : "determinate"} />
            {resourceIds.length > 1 && <span>{totalCompleted} / {resourceIds.length}</span>}
          </div>
        }
        {apiError &&
          <Alert severity={"error"}>
            {apiError.message} ({apiError.code} {apiError.server_message})
          </Alert>
        }

        <div style={{ marginTop: "1rem", display: "flex", justifyContent: "space-between" }}>
          <Button
            onClick={(e: any) => handleClose({}, "escapeKeyDown")}
            disabled={isRunning}
            color="inherit">
            {i18n.t("cancel")}
          </Button>

          <Button
            onClick={handleConfirmationClick}
            disabled={confirmationDisabled}
            color={ActionColors[props.action]}>
            {i18n.t(props.action)}
          </Button>
        </div>
      </DialogContent >
    </Dialog >
  );
}